import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  signal,
  Signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperNext,
  MatStepperPrevious
} from '@angular/material/stepper';
import { SharedModule } from '../../../../shared/shared.module';
import { PineStepperFooterComponent } from './pine-stepper-footer/pine-stepper-footer.component';
import {
  AccordionComponent,
  AccordionItemComponent,
  SwitchComponent
} from '@aetherion/ui';
import { ToPrecisionPipe } from '../../../../pipes/to-precision.pipe';
import { EskPopoverDirective } from '../../../../directives/esk-popover.directive';
import { PinePopoverComponent } from './pine-popover/pine-popover.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  getDefaultVal,
  getDefaultValObj,
  PeatDefaultValuesModels
} from './data-acces/peat-default-values.models';
import { toSignal } from '@angular/core/rxjs-interop';
import { CostWeight } from '../../../../models/PEAT Tool/cost-weight.model';
import { PropertyDexie } from '../../../../models/property.model';
import { PeatReport } from '../../../../models/PEAT Tool/peat-report.model';
import { PaddockDexie } from '../../../../models/paddock.model';
import { PineEconomicAnalysisReport } from '../../../../models/PEAT Tool/pine-economic-analysis-report.model';
import { PineEconomicAnalysisToolService } from '../../../../services/pine-economic-analysis-tool.service';
import { ThinningAndHarvestYield } from '../../../../models/PEAT Tool/thinning-and-harvest-yield.model';
import { ThinningAndHarvestAge } from '../../../../models/PEAT Tool/thinning-and-harvest-age.model';
import { PineEconomicAnalysisToolRow } from '../../../../models/PEAT Tool/pine-economic-analysis-tool-row.model';
import {
  Costs,
  PineEconomicAnalysisProjection,
  Returns
} from '../../../../models/PEAT Tool/pine-economic-analysis-projection.model';
import {
  BaseRow,
  CtrlSummaryConfig,
  FinancialInput,
  InputColumn,
  PeatSummaryReport,
  SiteQualityAndHarvest,
  TableInputRow,
  UndiscountedRevenueRow
} from '../../../../models/PEAT Tool/peat-summary-report.model';
import { irr } from 'node-irr';
import { RoundingPipe } from '../../../../pipes/rounding.pipe';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import {
  addFooter,
  addHeader,
  addParagraph,
  addSubHeader,
  jsPDFCustom
} from '../../../../interfaces/JsPDF.extended.interface';
import {
  ensureSpace,
  getPeatOverViewSection,
  PDFTable,
  PeatPDFSections,
  ReportText
} from './data-acces/peat-pdf-data';

Chart.register(...registerables);

@Component({
  selector: 'app-pine-economic-analysis-tool',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatStepper,
    MatStep,
    SharedModule,
    MatStepLabel,
    MatStepperPrevious,
    MatStepperNext,
    PineStepperFooterComponent,
    SwitchComponent,
    ToPrecisionPipe,
    EskPopoverDirective,
    PinePopoverComponent,
    RoundingPipe,
    AccordionComponent,
    AccordionItemComponent
  ],
  templateUrl: './pine-economic-analysis-tool.component.html',
  styleUrl: './pine-economic-analysis-tool.component.css'
})
export class PineEconomicAnalysisToolComponent implements OnDestroy {
  peatReport = inject<PeatReport>(MAT_DIALOG_DATA);
  private peatService = inject(PineEconomicAnalysisToolService);
  locationDetails: string;
  readonly formBuilder: FormBuilder = inject(FormBuilder);

  public showEstablishmentDetailedCost: boolean = false;
  public showPostEstablishmentDetailedCost: boolean = false;
  public modelCarbonPlantation: boolean = true;
  public modelCommercialThinning: WritableSignal<boolean> = signal(true);
  showSummary = false;
  siteQualityDetails: SiteQualityAndHarvest[];
  thinAndHarvest: SiteQualityAndHarvest[];
  defaultValues: PeatDefaultValuesModels;

  @ViewChild('stepper') private peatStepper: MatStepper;

  private readonly _xArr = Array.from({ length: 32 }, (_, i) => i);
  private cashflowChart?: Chart;
  private carbonHarvestChart?: Chart;

  @ViewChild('cashflowCanvas')
  set cashflowCanvas(canvas: ElementRef<HTMLCanvasElement> | undefined) {
    if (!canvas) return;

    this._setupCashflowCharts(canvas);
  }

  @ViewChild('carbonHarvestCanvas')
  set carbonHarvestCanvas(canvas: ElementRef<HTMLCanvasElement> | undefined) {
    if (!canvas) return;

    this._setupCarbonHarvestCanvasCharts(canvas);
  }

  private readonly _target: PropertyDexie | PaddockDexie;
  private readonly _report: PineEconomicAnalysisReport;
  private readonly _thinningHarvestAges: ThinningAndHarvestAge[];
  private readonly _thinningHarvestYields: ThinningAndHarvestYield[];
  private readonly injector = inject(Injector);

  private _peatProjection: PineEconomicAnalysisProjection;

  private readonly _right = InputColumn.RIGHT_CLS;
  private readonly _left = InputColumn.LEFT_CLS;

  readonly establishmentControls = [
    'sitePreparation',
    'weedControl',
    'seedlings',
    'planting',
    'fertilising',
    'gameControl'
  ];
  readonly postEstablishmentControls = [
    'weedControl',
    'refillPlanting',
    'gameControl',
    'complianceReporting'
  ];
  readonly carbonControls = [
    'establishmentCost',
    'annualOffset',
    'audit',
    'accu'
  ];
  readonly thinningControls = [
    'harvestCost.smallSawlogs',
    'harvestCost.posts',
    'harvestCost.pulplogs',
    'distanceToMill.smallSawlogs',
    'distanceToMill.posts',
    'distanceToMill.pulplogs',
    'cartageCost.smallSawlogs',
    'cartageCost.posts',
    'cartageCost.pulplogs',
    'logPrice.smallSawlogs',
    'logPrice.posts',
    'logPrice.pulplogs',
    'fpp',
    'feesAndLevies',
    'roadMaintenance'
  ];
  readonly harvestControls = [
    'harvestCost.largeSawlogs',
    'harvestCost.smallSawlogs',
    'harvestCost.pulplogs',
    'distanceToMill.largeSawlogs',
    'distanceToMill.smallSawlogs',
    'distanceToMill.pulplogs',
    'cartageCost.largeSawlogs',
    'cartageCost.smallSawlogs',
    'cartageCost.pulplogs',
    'logPrice.largeSawlogs',
    'logPrice.smallSawlogs',
    'logPrice.pulplogs',
    'fpp',
    'feesAndLevies',
    'roadMaintenance'
  ];

  chartCanvasOne: ElementRef<HTMLCanvasElement>;
  chartCanvasTwo: ElementRef<HTMLCanvasElement>;
  peatSummary: PeatSummaryReport;

  assumedThinningAge: number;
  assumedHarvestAge: number;
  thinRegime: string = '';

  thinningVolumes: Map<string, number>;
  harvestVolumes: Map<string, number>;

  totalThinningVolume: number;
  totalHarvestVolume: number;

  locationAndPlantation: FormGroup = this.formBuilder.group({
    areaToBePlanted: [
      100,
      [Validators.required, Validators.min(5), Validators.max(1000)]
    ]
  });

  areaToBePlantedSignal: Signal<number> = toSignal(
    this.locationAndPlantation.get('areaToBePlanted').valueChanges
  );
  useDefaultLocation: Signal<boolean>;

  /** Establishment Controls */
  establishment: FormGroup;
  establishmentFgSignal: Signal<any>;
  allInclusiveCostSignal: Signal<number>;
  establishmentTotalSignal: Signal<any>;
  useEstablishmentDefault: Signal<boolean>;

  /** Post Establishment Controls */
  postEstablishment: FormGroup;
  postEstablishmentFgSignal: Signal<any>;
  totalSignal: Signal<number>;
  postEstablishmentTotalSignal: Signal<any>;
  usePostEstablishmentDefault: Signal<boolean>;

  /** Carbon Controls */
  carbon: FormGroup;
  carbonFgSignal: Signal<any>;
  useCarbonDefault: Signal<boolean>;

  /** Thinning Controls */
  thinning: FormGroup;
  thinningFgSignal: Signal<any>;
  useThinningDefault: Signal<boolean>;

  /** Harvest Controls */
  harvest: FormGroup;
  harvestFgSignal: Signal<any>;
  useHarvestDefault: Signal<boolean>;

  /** Financial Controls */
  financial: FormGroup;
  financialFgSignal: Signal<any>;
  useFinancialDefault: Signal<boolean>;

  thinningData: TableRow[];
  harvestData: TableRow[];

  private readonly _establishmentDefaultCostWeights: CostWeight = {
    sitePreparation: 0.333333333333333,
    weedControl: 0.0888888888888889,
    seedlings: 0.222222222222222,
    planting: 0.222222222222222,
    fertilising: 0.0888888888888889,
    gameControl: 0.0444444444444444
  };

  private readonly _postEstablishmentDefaultCostWeights: CostWeight = {
    weedControl: 0.373134328358209,
    refillPlanting: 0.373134328358209,
    gameControl: 0.223880597014925,
    complianceReporting: 0.0298507462686567
  };

  ngOnDestroy() {
    this.cashflowCanvas;
  }

  constructor(private cdr: ChangeDetectorRef) {
    this._target = this.peatReport.target;
    this._report = this.peatReport.report;
    this._thinningHarvestAges = this.peatReport.thinningHarvestAges;
    this._thinningHarvestYields = this.peatReport.thinningHarvestYields;
    this.thinningVolumes = new Map<string, number>();
    this.harvestVolumes = new Map<string, number>();
    this.defaultValues = this.peatReport.defaultValues;

    const peatType: string =
      this._target instanceof PropertyDexie ? 'Property' : 'Paddock';

    if (this._report == null) {
      this._report = this.peatService.getDummyObject();
    }
    if (peatType == 'Property') {
      this._report.propertyID = this._target.propertyID;
    } else {
      this._report.paddockID = (this._target as PaddockDexie).paddockID;
    }
    this.assumedThinningAge = this._report.thinAge;
    this.assumedHarvestAge = this._report.harvestAge;

    this.locationDetails = `${peatType}: ${this._target.name}`;
    this.locationAndPlantation
      .get('areaToBePlanted')
      .patchValue(this._target.area);

    this.useDefaultLocation = computed(
      () => this.areaToBePlantedSignal() === this._target.area
    );

    this._createAndInitialiseForm();
    const thinningDefaults = this._getThinningDefaults();
    this.thinning.patchValue(thinningDefaults, {
      emitEvent: false
    });

    this.useThinningDefault = this._matchesToDefaultNested(
      this.thinning,
      this.thinningControls,
      thinningDefaults
    );

    const harvestDefaults = this._getHarvestDefaults();

    this.harvest.patchValue(harvestDefaults, {
      emitEvent: false
    });

    this.useHarvestDefault = this._matchesToDefaultNested(
      this.harvest,
      this.harvestControls,
      harvestDefaults
    );

    this._bindCalcSum(
      this.establishment,
      'allInclusiveCost',
      this.establishmentControls
    );

    this._bindCalcSum(
      this.postEstablishment,
      'total',
      this.postEstablishmentControls
    );

    effect(() => {
      const total = this.allInclusiveCostSignal();

      if (total == null || this.showEstablishmentDetailedCost) {
        return;
      }

      for (const [ctrlName, costWeight] of Object.entries(
        this._establishmentDefaultCostWeights
      )) {
        this._calculateAndSetControlValue(
          this.establishment,
          ctrlName,
          total,
          costWeight
        );
      }
    });

    effect(() => {
      const total: number = this.totalSignal();

      if (total == null || this.showPostEstablishmentDetailedCost) {
        return;
      }

      for (const [ctrlName, costWeight] of Object.entries(
        this._postEstablishmentDefaultCostWeights
      )) {
        this._calculateAndSetControlValue(
          this.postEstablishment,
          ctrlName,
          total,
          costWeight
        );
      }
    });

    effect(() => {
      this._thinningVolume();
      this._harvestVolume();
    });

    if (this._report.thinAge === -1) {
      this.modelCommercialThinning.set(false);
    }

    const {
      wellSuited,
      suitable,
      moderatelySuitable,
      unsuitable,
      thinAge,
      harvestAge
    } = this._report;

    this.siteQualityDetails = [
      new SiteQualityAndHarvest('Well Suited Area:', wellSuited, 'Ha'),
      new SiteQualityAndHarvest('Suitable Area:', suitable, 'Ha'),
      new SiteQualityAndHarvest(
        'Moderately Suitable Area:',
        moderatelySuitable,
        'Ha'
      ),
      new SiteQualityAndHarvest('Unsuitable Area:', unsuitable, 'Ha')
    ];

    this.thinAndHarvest = [
      new SiteQualityAndHarvest(
        'Thinning',
        thinAge != -1 ? thinAge : 'NA',
        'years old'
      ),
      new SiteQualityAndHarvest('Harvest', harvestAge, 'years old')
    ];

    effect(() => {
      if (
        this.showSummary &&
        (this.areaToBePlantedSignal() ||
          this.establishmentFgSignal() ||
          this.postEstablishmentFgSignal() ||
          this.carbonFgSignal() ||
          this.thinningFgSignal() ||
          this.harvestFgSignal() ||
          this.harvestFgSignal() ||
          this.financialFgSignal())
      ) {
        this.runReport(false);
      }
    });
  }

  get siteQuality() {
    return this._report.siteQuality;
  }

  get bioregion() {
    return this._report.bioregion;
  }

  get coordinates() {
    return `${this._report.coordinates[1].toFixed(
      6
    )}, ${this._report.coordinates[0].toFixed(6)}`;
  }

  get isACCUError() {
    const isAccuError =
      this._report.pineEconomicAnalysisAccuYieldRows.filter(
        (s) => s.status === 'Completed'
      ).length === 0;

    if (isAccuError) {
      this.carbon.disable();
    } else if (!isAccuError && this.modelCarbonPlantation) {
      this.carbon.enable();
    }

    return isAccuError;
  }

  get accuArray() {
    return this.modelCarbonPlantation
      ? this._peatProjection.yields.accu
      : new Array(this._peatProjection.yields.accu.length).fill(0);
  }

  getSwitchStatusText(switchCtrl: any) {
    return switchCtrl ? 'YES' : 'NO';
  }

  setUseLocationDefault($event: any) {
    if ($event) {
      this.locationAndPlantation
        .get('areaToBePlanted')
        .patchValue(this._target.area);
    }
  }

  setShowEstablishmentDetailedCost($event: any) {
    this.showEstablishmentDetailedCost = $event;
    $event
      ? this.establishment.get('allInclusiveCost').disable({ emitEvent: false })
      : this.establishment.get('allInclusiveCost').enable({ emitEvent: false });
  }

  setUseEstablishmentDefault($event: any) {
    if ($event) {
      this.establishment.patchValue(this.getDefaultVal('establishment'));
    }
  }

  setShowPostEstablishmentDetailedCost($event: any) {
    this.showPostEstablishmentDetailedCost = $event;
    $event
      ? this.postEstablishment.get('total').disable({ emitEvent: false })
      : this.postEstablishment.get('total').enable({ emitEvent: false });
  }

  setUsePostEstablishmentDefault($event: any) {
    if ($event) {
      this.postEstablishment.patchValue(
        this.getDefaultVal('postEstablishment')
      );
    }
  }

  setModelCarbonPlantation($event: any) {
    this.modelCarbonPlantation = $event;
    $event ? this.carbon.enable() : this.carbon.disable();
  }

  setUseCarbonDefault($event: any) {
    if ($event) {
      this.carbon.patchValue(this.getDefaultVal('carbon'));
    }
  }

  async recalculateACCU() {
    const accu = await this.peatService.getAccuData(this._report.coordinates);
    this._report.pineEconomicAnalysisAccuYieldRows =
      this.peatService.processACCUData(accu);

    this.peatService.updateReportACCU(
      this._report,
      this._report.pineEconomicAnalysisAccuYieldRows
    );
  }

  setModelCommercialThinning($event: any) {
    this.modelCommercialThinning.set($event);

    const ctrlsToEnableDisable = [
      'harvestCost',
      'distanceToMill',
      'cartageCost',
      'logPrice',
      'fpp',
      'feesAndLevies',
      'roadMaintenance'
    ];

    for (const ctrl of ctrlsToEnableDisable) {
      this.modelCommercialThinning()
        ? this.thinning.get(ctrl).enable()
        : this.thinning.get(ctrl).disable();
    }
  }

  setUseThinningDefault($event: any) {
    if ($event) {
      this.thinning.patchValue(this._getThinningDefaults());
    }

    const ctrls = this._getThinningCtrls();
    this._defaultDistanceVal(this.thinning, ctrls);
  }

  setUseHarvestDefault($event: any) {
    if ($event) {
      this.harvest.patchValue(this._getHarvestDefaults());
    }

    const ctrls = this._getHarvestCtrls();
    this._defaultDistanceVal(this.harvest, ctrls);
  }

  setUseFinancialDefault($event: any) {
    if ($event) {
      this.financial.patchValue(this.getDefaultVal('financial'));
    }
  }

  runReport(navigateToPage: boolean = true) {
    const targetArea = this._getFormValue(
      this.locationAndPlantation,
      'areaToBePlanted'
    );
    const calcCarbon = this.modelCarbonPlantation ? 1 : 0;

    const regime =
      this.thinRegime === 'TH'
        ? `${this.thinRegime}${this.assumedThinningAge}`
        : this.thinRegime;
    const accuYiledT = `RAD_${regime}_CF${this.assumedHarvestAge}`;

    const carbonAccu = this._getFormValue(this.carbon, 'accu');

    const { pineEconomicAnalysisAccuYieldRows } = this._report;

    //Yields
    const accuYields = pineEconomicAnalysisAccuYieldRows.find(
      (p) => p.accuYield === accuYiledT
    );

    const discountRate =
      this._getFormValue(this.financial, 'discountRate') / 100;

    this._peatProjection = new PineEconomicAnalysisProjection(31);

    const pEstAnnualM = this._getFormValue(
      this.postEstablishment,
      'annualMaintenance'
    );
    const carbonAudit = this._getFormValue(this.carbon, 'audit');
    const carbonAnnualOffset = this._getFormValue(this.carbon, 'annualOffset');

    //Thinning
    const thinningConfig = ['smallSawlogs', 'posts', 'pulplogs'];
    const thinningCosts = this._getCosts(
      thinningConfig,
      this.thinning,
      targetArea,
      this.thinningVolumes,
      this.totalThinningVolume
    );

    const thinningReturns = this._getReturns(
      thinningConfig,
      this.thinning,
      targetArea,
      this.thinningVolumes
    );

    //Harvest
    const harvestConfig = ['largeSawlogs', 'smallSawlogs', 'pulplogs'];
    const harvestCosts = this._getCosts(
      harvestConfig,
      this.harvest,
      targetArea,
      this.harvestVolumes,
      this.totalHarvestVolume
    );

    const harvestReturns = this._getReturns(
      harvestConfig,
      this.harvest,
      targetArea,
      this.harvestVolumes
    );

    const isCarbon = this.modelCarbonPlantation;

    accuYields.accuYieldAgeOfPlantation.push(
      ...Array(31 - accuYields.accuYieldAgeOfPlantation.length).fill(0)
    );

    accuYields.accuYieldAgeOfPlantation.forEach((accu, index) => {
      const largeSawlog =
        targetArea * this._getLogTypeSum('largeSawlogs', index);
      const smallSawlog =
        targetArea * this._getLogTypeSum('smallSawlogs', index);
      const post = targetArea * this._getLogTypeSum('posts', index);
      const pulplog = targetArea * this._getLogTypeSum('pulplogs', index);
      const total = largeSawlog + smallSawlog + post + pulplog;

      const isThinningAge = index === this.assumedThinningAge;
      const isHarvestAge = index === this.assumedHarvestAge;

      this._peatProjection.yields.accu[index] = accu;
      this._peatProjection.yields.largeSawlogs[index] = largeSawlog;
      this._peatProjection.yields.smallSawlogs[index] = smallSawlog;
      this._peatProjection.yields.posts[index] = post;
      this._peatProjection.yields.pulplogs[index] = pulplog;
      this._peatProjection.yields.totalLogProducts[index] = total;

      //only 0
      this._peatProjection.costs.estAgeZeroFixedCosts[index] =
        index === 0
          ? -this._getFormValue(this.establishment, 'fpp') -
            this._getFormValue(this.establishment, 'ptr')
          : 0;

      const allInclusive =
        index === 0
          ? this._getFormValue(this.establishment, 'allInclusiveCost')
          : 0;
      const eFAndL =
        index === 0
          ? this._getFormValue(this.establishment, 'feesAndLevies')
          : 0;

      this._peatProjection.costs.estAgeZeroAreaBasedCosts[index] =
        -(allInclusive + eFAndL) * targetArea;

      this._peatProjection.costs.carbonAgeZeroFixedCosts[index] =
        index === 0 && isCarbon
          ? -this._getFormValue(this.carbon, 'establishmentCost')
          : 0;

      //only 1 Post Establishment
      const pEstTot =
        index === 1 ? this._getFormValue(this.postEstablishment, 'total') : 0;
      this._peatProjection.costs.postEstAgeOneAreaBasedCosts[index] =
        -pEstTot * targetArea;

      this._peatProjection.costs.carbonAgeOneAuditCosts[index] =
        index === 1 && isCarbon ? -carbonAudit * calcCarbon : 0;

      // 6 and 11
      const areaCon = targetArea >= 200 ? carbonAudit : 0;

      this._peatProjection.costs.carbonAgeSixAuditCosts[index] =
        index === 6 && isCarbon ? -areaCon * calcCarbon : 0;

      this._peatProjection.costs.carbonAgeElevenAuditCosts[index] =
        index === 11 && isCarbon ? -areaCon * calcCarbon : 0;

      if (index != 0) {
        this._peatProjection.costs.postEstAnnualAreaBasedCosts[index] =
          -pEstAnnualM * targetArea;

        this._peatProjection.costs.carbonOffsetReportCosts[index] =
          accu > 0 && this.modelCarbonPlantation
            ? -carbonAnnualOffset * calcCarbon
            : 0;
      }

      this._peatProjection.costs.thinningHarvestCosts[index] = isThinningAge
        ? thinningCosts.harvestCost
        : 0;
      this._peatProjection.costs.thinningCartageCosts[index] = isThinningAge
        ? thinningCosts.cartageCost
        : 0;
      this._peatProjection.costs.thinningFixedPlanningCosts[index] =
        isThinningAge ? thinningCosts.fixedPlanningCost : 0;
      this._peatProjection.costs.thinningAreaBasedPlanningCosts[index] =
        isThinningAge ? thinningCosts.areaBasedPlanningCost : 0;
      this._peatProjection.costs.thinningRoadingCosts[index] = isThinningAge
        ? thinningCosts.roadingCost
        : 0;

      this._peatProjection.costs.harvestCosts[index] = isHarvestAge
        ? harvestCosts.harvestCost
        : 0;
      this._peatProjection.costs.harvestCartageCosts[index] = isHarvestAge
        ? harvestCosts.cartageCost
        : 0;
      this._peatProjection.costs.harvestFixedPlanningCosts[index] = isHarvestAge
        ? harvestCosts.fixedPlanningCost
        : 0;
      this._peatProjection.costs.harvestAreaBasedPlanningCosts[index] =
        isHarvestAge ? harvestCosts.areaBasedPlanningCost : 0;
      this._peatProjection.costs.harvestRoadingCosts[index] = isHarvestAge
        ? harvestCosts.roadingCost
        : 0;

      //Returns
      this._peatProjection.returns.carbonAccuReturns[index] =
        targetArea * calcCarbon * carbonAccu * accu;

      this._peatProjection.returns.thinningSmallSawlogsReturns[index] =
        isThinningAge ? thinningReturns[0] : 0;

      this._peatProjection.returns.thinningPostsReturns[index] = isThinningAge
        ? thinningReturns[1]
        : 0;

      this._peatProjection.returns.thinningPulplogsReturns[index] =
        isThinningAge ? thinningReturns[2] : 0;

      this._peatProjection.returns.harvestLargeSawlogsReturns[index] =
        isHarvestAge ? harvestReturns[0] : 0;

      this._peatProjection.returns.harvestSmallSawlogsReturns[index] =
        isHarvestAge ? harvestReturns[1] : 0;

      this._peatProjection.returns.harvestPulplogsReturns[index] = isHarvestAge
        ? harvestReturns[2]
        : 0;

      const undiscountedCost = this._sumAtIndex(
        this._peatProjection.costs,
        index
      );
      this._peatProjection.undiscountedCosts[index] = undiscountedCost;

      const undiscountedReturn = this._sumAtIndex(
        this._peatProjection.returns,
        index
      );
      this._peatProjection.undiscountedReturns[index] = undiscountedReturn;

      const cashFlow = undiscountedCost + undiscountedReturn;
      this._peatProjection.undiscountedCashflow[index] =
        undiscountedCost + undiscountedReturn;

      this._peatProjection.undiscountedCumulativeCashflow[index] =
        index === 0
          ? cashFlow
          : this._peatProjection.undiscountedCumulativeCashflow[index - 1] +
            cashFlow;

      /* UNDISCOUNTED CASH FLOW */
      this._peatProjection.discountedCosts[index] =
        undiscountedCost / (1 + discountRate) ** index;

      /* UNDISCOUNTED CUMULATIVE CASH FLOW */
      this._peatProjection.discountedReturns[index] =
        undiscountedReturn / (1 + discountRate) ** index;
    });

    const totCosts = this._calcArraySum(this._peatProjection.discountedCosts);
    const totReturns = this._calcArraySum(
      this._peatProjection.discountedReturns
    );

    const {
      costs,
      returns,
      undiscountedCashflow,
      discountedCosts,
      discountedReturns
    } = this._peatProjection;

    const costsSummary = new Map<string, BaseRow>();
    const revenueSummary = new Map<string, UndiscountedRevenueRow>();

    const est = this._calcTotalAndPerHa(
      costs.estAgeZeroAreaBasedCosts,
      targetArea
    );
    costsSummary.set('Establishment', est);

    const estPlan = this._calcTotalAndPerHa(
      costs.estAgeZeroFixedCosts,
      targetArea
    );
    costsSummary.set('Establishment planning', estPlan);

    const postEst = this._calcTotalAndPerHa(
      costs.postEstAgeOneAreaBasedCosts,
      targetArea
    );
    costsSummary.set('Post Establishment', postEst);

    const annulMaintenance = this._calcTotalAndPerHa(
      costs.postEstAnnualAreaBasedCosts,
      targetArea
    );
    costsSummary.set('Annual Maintenance', annulMaintenance);
    costsSummary.set('', null);

    const carbonEst = this._calcTotalAndPerHa(
      costs.carbonAgeZeroFixedCosts,
      targetArea
    );
    costsSummary.set('Carbon Establishment Cost', carbonEst);

    const carbonComp = this._calcTotalAndPerHa(
      [
        ...costs.carbonAgeOneAuditCosts,
        ...costs.carbonAgeSixAuditCosts,
        ...costs.carbonAgeElevenAuditCosts,
        ...costs.carbonOffsetReportCosts
      ],
      targetArea
    );
    costsSummary.set('Carbon Compliance Cost', carbonComp);

    const thinningHarvest = this._calcTotalAndPerHa(
      costs.thinningHarvestCosts,
      targetArea
    );
    costsSummary.set('', null);

    costsSummary.set('Thinning Harvest', thinningHarvest);

    const thinningCartage = this._calcTotalAndPerHa(
      costs.thinningCartageCosts,
      targetArea
    );
    costsSummary.set('Thinning Cartage', thinningCartage);

    const thinningPlanning = this._calcTotalAndPerHa(
      [
        ...costs.thinningFixedPlanningCosts,
        ...costs.thinningAreaBasedPlanningCosts
      ],
      targetArea
    );
    costsSummary.set('Thinning Planning', thinningPlanning);

    const thinningRoading = this._calcTotalAndPerHa(
      costs.thinningRoadingCosts,
      targetArea
    );

    costsSummary.set('Thinning road maintenance', thinningRoading);

    costsSummary.set('', null);

    const harvest = this._calcTotalAndPerHa(costs.harvestCosts, targetArea);
    costsSummary.set('Harvesting', harvest);

    const harvestCartage = this._calcTotalAndPerHa(
      costs.harvestCartageCosts,
      targetArea
    );
    costsSummary.set('Harvest Cartage', harvestCartage);

    const harvestPlanning = this._calcTotalAndPerHa(
      [
        ...costs.harvestFixedPlanningCosts,
        ...costs.harvestAreaBasedPlanningCosts
      ],
      targetArea
    );
    costsSummary.set('Harvest Planning', harvestPlanning);

    const harvestRoading = this._calcTotalAndPerHa(
      costs.harvestRoadingCosts,
      targetArea
    );
    costsSummary.set('Harvest Road Maintenance', harvestRoading);

    const costsArr = [
      est.total,
      estPlan.total,
      postEst.total,
      annulMaintenance.total,
      carbonEst.total,
      carbonComp.total,
      thinningHarvest.total,
      thinningCartage.total,
      thinningPlanning.total,
      thinningRoading.total,
      harvest.total,
      harvestCartage.total,
      harvestPlanning.total,
      harvestRoading.total
    ];

    const totCost = this._calcTotalAndPerHa(costsArr, targetArea);

    const accuUnitsHa = isCarbon
      ? this._calcArraySum(accuYields.accuYieldAgeOfPlantation)
      : 0;
    const accuTotPerHa = this._calcTotalAndPerHa(
      returns.carbonAccuReturns,
      targetArea
    );
    const accuYieldRevenue = new UndiscountedRevenueRow(
      'ACCU Yield',
      accuTotPerHa.total,
      accuTotPerHa.perHa,
      accuUnitsHa
    );
    revenueSummary.set('ACCU Yield', accuYieldRevenue);

    const tPOneTonnes = this.thinningVolumes.get('smallSawlogs') * targetArea;
    const tPOneTotPerHa = this._calcTotalAndPerHa(
      returns.thinningSmallSawlogsReturns,
      targetArea
    );
    const tPOneRevenue = new UndiscountedRevenueRow(
      'Small Sawlogs',
      tPOneTotPerHa.total,
      tPOneTotPerHa.perHa,
      tPOneTonnes
    );
    revenueSummary.set('Thinning Product 1', tPOneRevenue);

    const tPTwoTonnes = this.thinningVolumes.get('posts') * targetArea;
    const tPTwoTotPerHa = this._calcTotalAndPerHa(
      returns.thinningPostsReturns,
      targetArea
    );
    const tPTwoRevenue = new UndiscountedRevenueRow(
      'Posts',
      tPTwoTotPerHa.total,
      tPTwoTotPerHa.perHa,
      tPTwoTonnes
    );
    revenueSummary.set('Thinning Product 2', tPTwoRevenue);

    const tPThreeTonnes = this.thinningVolumes.get('pulplogs') * targetArea;
    const tPThreeTotPerHa = this._calcTotalAndPerHa(
      returns.thinningPulplogsReturns,
      targetArea
    );
    const tPThreeRevenue = new UndiscountedRevenueRow(
      'Pulplogs',
      tPThreeTotPerHa.total,
      tPThreeTotPerHa.perHa,
      tPThreeTonnes
    );
    revenueSummary.set('Thinning Product 3', tPThreeRevenue);

    const tTotalTonnes = this._calcArraySum([
      tPOneTonnes,
      tPTwoTonnes,
      tPThreeTonnes
    ]);

    const tSumTotPerHa = this._calcTotalAndPerHa(
      [tPOneTotPerHa.total, tPTwoTotPerHa.total, tPThreeTotPerHa.total],
      targetArea
    );

    revenueSummary.set(
      'Thinning Total',
      new UndiscountedRevenueRow(
        'Thinning Total',
        tSumTotPerHa.total,
        tSumTotPerHa.perHa,
        tTotalTonnes,
        true
      )
    );

    revenueSummary.set('', null);

    const hPOneTonnes = this.harvestVolumes.get('largeSawlogs') * targetArea;
    const hPOneTotPerHa = this._calcTotalAndPerHa(
      returns.harvestLargeSawlogsReturns,
      targetArea
    );
    const hPOneRevenue = new UndiscountedRevenueRow(
      'Large Sawlogs',
      hPOneTotPerHa.total,
      hPOneTotPerHa.perHa,
      hPOneTonnes
    );
    revenueSummary.set('Harvest Product 1', hPOneRevenue);

    const hPTwoTonnes = this.harvestVolumes.get('smallSawlogs') * targetArea;
    const hPTwoTotPerHa = this._calcTotalAndPerHa(
      returns.harvestSmallSawlogsReturns,
      targetArea
    );
    const hPTwoRevenue = new UndiscountedRevenueRow(
      'Small Sawlogs',
      hPTwoTotPerHa.total,
      hPTwoTotPerHa.perHa,
      hPTwoTonnes
    );
    revenueSummary.set('Harvest Product 2', hPTwoRevenue);

    const hPThreeTonnes = this.harvestVolumes.get('pulplogs') * targetArea;
    const hPThreeTotPerHa = this._calcTotalAndPerHa(
      returns.harvestPulplogsReturns,
      targetArea
    );
    const hPThreeRevenue = new UndiscountedRevenueRow(
      'Pulplogs',
      hPThreeTotPerHa.total,
      hPThreeTotPerHa.perHa,
      hPThreeTonnes
    );
    revenueSummary.set('Harvest Product 3', hPThreeRevenue);

    const hTotalTonnes = this._calcArraySum([
      hPOneTonnes,
      hPTwoTonnes,
      hPThreeTonnes
    ]);

    const hSumTotPerHa = this._calcTotalAndPerHa(
      [hPOneTotPerHa.total, hPTwoTotPerHa.total, hPThreeTotPerHa.total],
      targetArea
    );

    revenueSummary.set(
      'Harvest Total',
      new UndiscountedRevenueRow(
        'Harvest Total',
        hSumTotPerHa.total,
        hSumTotPerHa.perHa,
        hTotalTonnes,
        true
      )
    );

    revenueSummary.set('', null);

    const totRevenue = this._calcTotalAndPerHa(
      [accuTotPerHa.total, tSumTotPerHa.total, hSumTotPerHa.total],
      targetArea
    );
    const sumDiscountedCosts = this._calcArraySum(discountedCosts);
    const sumDiscountedReturns = this._calcArraySum(discountedReturns);
    const npv = sumDiscountedCosts + sumDiscountedReturns;
    // const internalRR = irr(undiscountedCashflow, { estimate: discountRate });
    const internalRR = irr(undiscountedCashflow);

    const threeH = InputColumn.THREE_HEADINGS;
    const thinningH = InputColumn.THINNING_HEADINGS;
    const harvestH = InputColumn.HARVEST_HEADINGS;

    const financialInput: FinancialInput[] = [];

    this._addFinancialInput(
      financialInput,
      'Establishment Cost Inputs:',
      threeH,
      this._buildSimpleSummaryRows(this.establishment, [
        [
          'sitePreparation',
          new CtrlSummaryConfig('Site Preparation', 'per hectare')
        ],
        ['weedControl', new CtrlSummaryConfig('Weed Control', 'per hectare')],
        ['seedlings', new CtrlSummaryConfig('Seedlings', 'per hectare')],
        ['planting', new CtrlSummaryConfig('Planting', 'per hectare')],
        ['fertilising', new CtrlSummaryConfig('Fertilising', 'per hectare')],
        ['gameControl', new CtrlSummaryConfig('Game Control', 'per hectare')],
        ['fpp', new CtrlSummaryConfig('Forest Practice Plan', 'per plan')],
        [
          'feesAndLevies',
          new CtrlSummaryConfig('Fees and Levies', 'per hectare')
        ],
        ['ptr', new CtrlSummaryConfig('Private Timber Reserve', 'per site')]
      ])
    );

    this._addFinancialInput(
      financialInput,
      'Post-Establishment Cost Inputs:',
      threeH,
      this._buildSimpleSummaryRows(this.postEstablishment, [
        ['weedControl', new CtrlSummaryConfig('Weed Control', 'per hectare')],
        [
          'refillPlanting',
          new CtrlSummaryConfig('Refill Planting', 'per hectare')
        ],
        ['gameControl', new CtrlSummaryConfig('Game Control', 'per hectare')],
        [
          'complianceReporting',
          new CtrlSummaryConfig('Compliance Reporting', 'per plan')
        ],
        [
          'annualMaintenance',
          new CtrlSummaryConfig('Annual Maintenance', 'per hectare per year')
        ]
      ])
    );

    if (isCarbon) {
      this._addFinancialInput(
        financialInput,
        'Carbon Project Cost Inputs:',
        threeH,
        this._buildSimpleSummaryRows(this.carbon, [
          [
            'establishmentCost',
            new CtrlSummaryConfig('Registration', 'per project')
          ],
          ['annualOffset', new CtrlSummaryConfig('Compliance', 'per project')],
          [
            'audit',
            new CtrlSummaryConfig(
              'Audit',
              'per project (only once in first year if plantation < 200ha, additional two more at 5 years apart if plantation > 200ha)'
            )
          ]
        ])
      );

      this._addFinancialInput(
        financialInput,
        'Carbon Project Returns Inputs:',
        threeH,
        this._buildSimpleSummaryRows(this.carbon, [
          ['accu', new CtrlSummaryConfig('ACCU Sale Price', 'per ACCU')]
        ])
      );
    }

    const thinningProducts = ['smallSawlogs', 'posts', 'pulplogs'];
    const harvestProducts = ['largeSawlogs', 'smallSawlogs', 'pulplogs'];

    const logCostConfig: [string, CtrlSummaryConfig][] = [
      ['harvestCost', new CtrlSummaryConfig('Harvest Cost', 'per tonne')],
      [
        'distanceToMill',
        new CtrlSummaryConfig('Distance to Processor', 'kilometres')
      ],
      [
        'cartageCost',
        new CtrlSummaryConfig('Cartage Cost', 'per tonne per kilometre')
      ]
    ];

    const otherCostConfig: [string, CtrlSummaryConfig][] = [
      ['fpp', new CtrlSummaryConfig('Forest Practices Plan (FPP)', 'per plan')],
      [
        'feesAndLevies',
        new CtrlSummaryConfig('Fees and Levies', 'per hectare')
      ],
      [
        'roadMaintenance',
        new CtrlSummaryConfig('Road maintenance', 'per tonne')
      ]
    ];

    const logReturnsConfig: [string, CtrlSummaryConfig][] = [
      ['logPrice', new CtrlSummaryConfig('Log Price', 'per tonne')]
    ];

    if (this.modelCommercialThinning()) {
      this._addFinancialInput(
        financialInput,
        'Thinning Costs by Log Product:',
        thinningH,
        this._buildProductRows(this.thinning, logCostConfig, thinningProducts)
      );
      this._addFinancialInput(
        financialInput,
        'Other Thinning Costs:',
        threeH,
        this._buildSimpleSummaryRows(this.thinning, otherCostConfig)
      );

      this._addFinancialInput(
        financialInput,
        'Thinning Returns by Log Product:',
        thinningH,
        this._buildProductRows(
          this.thinning,
          logReturnsConfig,
          thinningProducts
        )
      );
    }

    this._addFinancialInput(
      financialInput,
      'Harvest Costs by Log Product:',
      harvestH,
      this._buildProductRows(this.harvest, logCostConfig, harvestProducts)
    );

    this._addFinancialInput(
      financialInput,
      'Other Harvest Costs:',
      threeH,
      this._buildSimpleSummaryRows(this.harvest, otherCostConfig)
    );

    this._addFinancialInput(
      financialInput,
      'Harvest Returns by Log Product:',
      harvestH,
      this._buildProductRows(this.harvest, logReturnsConfig, harvestProducts)
    );

    this.peatSummary = new PeatSummaryReport(
      ReportText.SUMMARY,
      `${this._target.name} ${targetArea} ha`,
      this.modelCommercialThinning() ? `${this.assumedThinningAge}` : 'None',
      this.assumedHarvestAge,
      isCarbon ? 'Yes' : 'No',
      ReportText.UNDISCOUNTED_COSTS,
      costsSummary,
      ReportText.UNDISCOUNTED_REVENUE,
      revenueSummary,
      totCost,
      totRevenue,
      ReportText.FINANCIAL_ANALYSIS,
      discountRate,
      internalRR,
      npv,
      ReportText.CASHFLOW_CHART,
      ReportText.REVENUE_CHART,
      financialInput,
      ReportText.DISCLAIMER
    );

    if (navigateToPage) {
      this.showSummary = true;
      this.cdr.detectChanges();
      this.peatStepper.next();
    }
  }

  backFromSummary() {
    this.showSummary = false;
  }

  exportToCsv() {
    const headings: string =
      'Field, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30';

    const accY = `ACCUS, ${this.accuArray.join(',')}`;
    const pLs = `Pine Large Sawlogs, ${this._peatProjection.yields.largeSawlogs.join(
      ','
    )}`;
    const sLs = `Pine Small Sawlogs, ${this._peatProjection.yields.smallSawlogs.join(
      ','
    )}`;
    const pPo = `Pine Posts, ${this._peatProjection.yields.posts.join(',')}`;
    const pPupl = `Pine Pulplogs, ${this._peatProjection.yields.pulplogs.join(
      ','
    )}`;

    const estFC = `Establishment Form: Age 0 Fixed Costs, ${this._peatProjection.costs.estAgeZeroFixedCosts.join(
      ','
    )}`;
    const estAgeBasedCost = `Establishment Form: Age 0 Area Based Costs, ${this._peatProjection.costs.estAgeZeroAreaBasedCosts.join(
      ','
    )}`;
    const pEstAgeBasedCost = `Post Establishment Form: Age 1 Area Based Costs, ${this._peatProjection.costs.postEstAgeOneAreaBasedCosts.join(
      ','
    )}`;
    const pEstAnnualAgeBasedCost = `Post Establishment Form: Annual Area Based Costs, ${this._peatProjection.costs.postEstAnnualAreaBasedCosts.join(
      ','
    )}`;
    const cFC = `Carbon Form: Age 0 Fixed Costs, ${this._peatProjection.costs.carbonAgeZeroFixedCosts.join(
      ','
    )}`;
    const cOneAudit = `Carbon Form: Age 1 Audit Costs, ${this._peatProjection.costs.carbonAgeOneAuditCosts.join(
      ','
    )}`;
    const cSixAudit = `Carbon Form: Age 6 Audit Costs, ${this._peatProjection.costs.carbonAgeSixAuditCosts.join(
      ','
    )}`;
    const cEleAudit = `Carbon Form: Age 11 Audit Costs, ${this._peatProjection.costs.carbonAgeElevenAuditCosts.join(
      ','
    )}`;
    const cReport = `Carbon Form: Offset Report Costs, ${this._peatProjection.costs.carbonOffsetReportCosts.join(
      ','
    )}`;

    const tHC = `Thinning Form: Harvest Costs, ${this._peatProjection.costs.thinningHarvestCosts.join(
      ','
    )}`;
    const tCC = `Thinning Form: Cartage Costs, ${this._peatProjection.costs.thinningCartageCosts.join(
      ','
    )}`;
    const tFPC = `Thinning Form: Fixed Planning Costs, ${this._peatProjection.costs.thinningFixedPlanningCosts.join(
      ','
    )}`;
    const tABPC = `Thinning Form: Area Based Planning Costs, ${this._peatProjection.costs.thinningAreaBasedPlanningCosts.join(
      ','
    )}`;
    const tRC = `Thinning Form: Roading Costs, ${this._peatProjection.costs.thinningRoadingCosts.join(
      ','
    )}`;
    const hC = `Harvest Form: Harvest Costs, ${this._peatProjection.costs.harvestCosts.join(
      ','
    )}`;
    const hCC = `Harvest Form: Cartage Costs, ${this._peatProjection.costs.harvestCartageCosts.join(
      ','
    )}`;
    const hFPC = `Harvest Form: Fixed Planning Costs, ${this._peatProjection.costs.harvestFixedPlanningCosts.join(
      ','
    )}`;
    const hABPC = `Harvest Form: Area Based Planning Costs, ${this._peatProjection.costs.harvestAreaBasedPlanningCosts.join(
      ','
    )}`;
    const hRC = `Harvest Form: Roading Costs, ${this._peatProjection.costs.harvestRoadingCosts.join(
      ','
    )}`;

    const rCarbons = `Carbon Form: ACCU Returns, ${this._peatProjection.returns.carbonAccuReturns.join(
      ','
    )}`;
    const rTSL = `Thinning Form: Small Sawlogs Returns, ${this._peatProjection.returns.thinningSmallSawlogsReturns.join(
      ','
    )}`;
    const rTpo = `Thinning Form: Posts Returns, ${this._peatProjection.returns.thinningPostsReturns.join(
      ','
    )}`;
    const rTpu = `Thinning Form: Pulplogs Returns, ${this._peatProjection.returns.thinningPulplogsReturns.join(
      ','
    )}`;
    const hTLL = `Harvest Form: Large Sawlogs Returns, ${this._peatProjection.returns.harvestLargeSawlogsReturns.join(
      ','
    )}`;
    const hTSL = `Harvest Form: Small Sawlogs Returns, ${this._peatProjection.returns.harvestSmallSawlogsReturns.join(
      ','
    )}`;
    const hTpu = `Harvest Form: Pulplogs Returns, ${this._peatProjection.returns.harvestPulplogsReturns.join(
      ','
    )}`;
    const unCos = `UNDISCOUNTED  COSTS, ${this._peatProjection.undiscountedCosts.join(
      ','
    )}`;
    const unRet = `UNDISCOUNTED  RETURNS, ${this._peatProjection.undiscountedReturns.join(
      ','
    )}`;
    const unCas = `UNDISCOUNTED  CASHFLOW, ${this._peatProjection.undiscountedCashflow.join(
      ','
    )}`;

    const unCCC = `UNDISCOUNTED CUMULATIVE CASHFLOW, ${this._peatProjection.undiscountedCumulativeCashflow.join(
      ','
    )}`;
    const dC = `DISCOUNTED COSTS, ${this._peatProjection.discountedCosts.join(
      ','
    )}`;
    const dR = `DISCOUNTED RETURNS, ${this._peatProjection.discountedReturns.join(
      ','
    )}`;

    const data = [
      headings,
      accY,
      pLs,
      sLs,
      pPo,
      pPupl,
      estFC,
      estAgeBasedCost,
      pEstAgeBasedCost,
      pEstAnnualAgeBasedCost,
      cFC,
      cOneAudit,
      cSixAudit,
      cEleAudit,
      cReport,
      tHC,
      tCC,
      tFPC,
      tABPC,
      tRC,
      hC,
      hCC,
      hFPC,
      hABPC,
      hRC,
      rCarbons,
      rTSL,
      rTpo,
      rTpu,
      hTLL,
      hTSL,
      hTpu,
      unCos,
      unRet,
      unCas,
      unCCC,
      dC,
      dR
    ];
    const blob = new Blob([data.join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });

    saveAs(blob, `npm_data_${new Date().toDateString()}.csv`);
  }

  export() {
    const pdf = new jsPDF() as jsPDFCustom;
    const margin = 10;
    const headerHeight = 30;
    const pageWidth = pdf.internal.pageSize.width;
    const textWidth = pageWidth - 2 * margin;

    let title = 'Pine Economic Analysis Report';
    const pdfImages: Map<string, string> = new Map<string, string>();
    pdfImages.set(
      'cashflowChart',
      this.chartCanvasOne.nativeElement.toDataURL('image/png')
    );
    pdfImages.set(
      'revenueChart',
      this.chartCanvasTwo.nativeElement.toDataURL('image/png')
    );

    const pdfTables: Map<string, PDFTable> = new Map<string, PDFTable>();

    let undiscountedCostsData = [];

    this.peatSummary.costs.forEach((val, key) => {
      if (key !== '') {
        undiscountedCostsData.push([key, val.totalTxt, val.perHaTxt]);
      }
    });
    undiscountedCostsData.push([
      'TOTAL COST',
      this.peatSummary.totalCost.totalTxt,
      this.peatSummary.totalCost.perHaTxt
    ]);
    pdfTables.set('undiscountedCost', {
      head: ['Costs', 'Total', 'per ha'],
      body: undiscountedCostsData
    });

    let undiscountedRevenueData = [];

    this.peatSummary.revenues.forEach((val, key) => {
      if (key !== '') {
        undiscountedRevenueData.push([
          key,
          val.tonnesOrUPHaTxt,
          val.totalTxt,
          val.perHaTxt
        ]);
      }
    });

    undiscountedRevenueData.push([
      'TOTAL REVENUE',
      '',
      this.peatSummary.totalRevenue.totalTxt,
      this.peatSummary.totalRevenue.perHaTxt
    ]);

    pdfTables.set('undiscountedRevenue', {
      head: ['Costs', 'units/ha or Tonnes', 'Total', 'per ha'],
      body: undiscountedRevenueData
    });

    pdfTables.set('financialAnalysis', {
      head: [],
      body: [
        ['Discount Rate', this.peatSummary.discountRateTxt],
        ['Internal Rate of Return', this.peatSummary.irrTxt],
        ['Net Present Value', this.peatSummary.npvTxt]
      ]
    });

    addHeader(title, pdf, margin, headerHeight);
    addFooter(pdf, margin);

    const paragraphNewPageCallback = () => {
      pdf.addPage();
      addHeader(title, pdf, margin, headerHeight);
      addFooter(pdf, margin);
      return headerHeight + margin;
    };

    let y = headerHeight + margin;
    const subHeadingSpacing = 12;
    y += margin;

    const { location, thinAge, harvestAge, carbonModelled } = this.peatSummary;
    const overviewSection = getPeatOverViewSection(
      location,
      thinAge,
      harvestAge,
      carbonModelled
    );

    y += addSubHeader(overviewSection.title, pdf, margin, y);
    y += margin;

    overviewSection.paragraphs.forEach((paragraph) => {
      y = addParagraph(
        pdf,
        paragraph,
        margin,
        y,
        textWidth,
        margin,
        paragraphNewPageCallback
      );
      y += 5; // Space between paragraphs
    });

    y += margin;

    paragraphNewPageCallback();
    y = headerHeight + margin;

    PeatPDFSections.forEach((section) => {
      y = ensureSpace(pdf, y, 40, margin, headerHeight, title);

      y += addSubHeader(section.heading, pdf, margin, y);

      y = addParagraph(
        pdf,
        section.paragraph,
        margin,
        y,
        textWidth,
        margin,
        paragraphNewPageCallback
      );

      y += 5;

      if (section.dataType === 'table') {
        const pdt = pdfTables.get(section.dataKey);
        if (!pdt) return;

        pdf.autoTable({
          ...(pdt.head?.some((h) => h?.trim?.()) ? { head: [pdt.head] } : {}),
          body: pdt.body,
          startY: y,
          margin: {
            top: headerHeight + margin,
            left: margin,
            right: margin,
            bottom: 20
          },
          pageBreak: 'auto',
          rowPageBreak: 'auto',
          didDrawPage: () => {
            addHeader(title, pdf, margin, headerHeight);
            addFooter(pdf, margin);
          }
        });

        y = pdf.lastAutoTable.finalY + 10;
      } else {
        const imageHeight = 90;

        y = ensureSpace(pdf, y, imageHeight, margin, headerHeight, title);

        pdf.addImage(
          pdfImages.get(section.dataKey)!,
          'PNG',
          margin,
          y,
          140,
          imageHeight
        );

        y += imageHeight + 10;
      }
    });

    y += addSubHeader('Summary of Financial Inputs', pdf, margin, y);
    y += 10;

    this.peatSummary.financialInputs.forEach((input) => {
      y = ensureSpace(pdf, y, 50, margin, headerHeight, title);

      y = addParagraph(
        pdf,
        input.heading,
        margin,
        y,
        textWidth,
        margin,
        paragraphNewPageCallback
      );

      y += 5;

      const head = input.tableHeadings.map((th) => th.heading);
      const data = input.summary.map((tic) => tic.map((s) => s.heading));

      pdf.autoTable({
        ...(head?.some((h) => h?.trim?.()) ? { head: [head] } : {}),
        body: data,
        startY: y,
        margin: {
          top: headerHeight + margin,
          left: margin,
          right: margin,
          bottom: 20
        },
        pageBreak: 'auto',
        rowPageBreak: 'auto',
        didDrawPage: () => {
          addHeader(title, pdf, margin, headerHeight);
          addFooter(pdf, margin);
        }
      });

      // IMPORTANT
      y = pdf.lastAutoTable.finalY + 14;
    });

    addParagraph(
      pdf,
      ReportText.DISCLAIMER,
      margin,
      y,
      textWidth,
      margin,
      paragraphNewPageCallback
    );

    pdf.save(
      `pine-economic-analysis-report-${this._target.name.replace(
        ' ',
        '-'
      )}-${new Date().toUTCString()}.pdf`
    );
  }

  getDefaultVal(key: string): any {
    const [group, ctrl] = key.split('.');

    const groupDefault = this.defaultValues[group];

    if (groupDefault === undefined || groupDefault === null) {
      throw new Error(`PEAt defaults not loaded for group: ${group}`);
    }
    const defaultVal = getDefaultVal(groupDefault);

    return ctrl ? defaultVal[ctrl] : this._removeNullValues(defaultVal);
  }

  private _buildSimpleSummaryRows(
    form: FormGroup,
    configs: [string, CtrlSummaryConfig][]
  ): TableInputRow {
    return configs.map(([ctrl, config]) => [
      new InputColumn(config.item, this._left),
      new InputColumn(this._getFormValue(form, ctrl), this._right),
      new InputColumn(config.unit, this._right)
    ]);
  }

  private _buildProductRows(
    form: FormGroup,
    configs: [string, CtrlSummaryConfig][],
    products: string[]
  ): TableInputRow {
    return configs.map(([ctrl, config]) => [
      new InputColumn(config.item, this._left),
      ...products.map(
        (prod) =>
          new InputColumn(
            this._getFormValue(form, `${ctrl}.${prod}`),
            this._right
          )
      ),
      new InputColumn(config.unit, this._right)
    ]);
  }

  private _addFinancialInput(
    financialInput: FinancialInput[],
    heading: string,
    tableHeadings: InputColumn[],
    summary: TableInputRow
  ) {
    financialInput.push({
      heading,
      tableHeadings,
      summary
    });
  }

  private _setupCashflowCharts(canvas: ElementRef<HTMLCanvasElement>) {
    this.chartCanvasOne = canvas;
    this.cashflowChart?.destroy();

    const existingChart = Chart.getChart(canvas.nativeElement);
    existingChart?.destroy();

    const config: ChartConfiguration = {
      type: 'bar',
      data: {
        datasets: [
          {
            label: 'Undiscounted Cashflow',
            data: this._peatProjection.undiscountedCashflow
          },
          {
            type: 'line',
            label: 'Undiscounted Cumulative Cashflow',
            data: this._peatProjection.undiscountedCumulativeCashflow
          }
        ],
        labels: this._xArr
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Cashflow'
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Plantation Age (Years)'
            }
          },
          y: {
            title: {
              display: true,
              text: 'Net Cashflow ($)'
            }
          }
        }
      }
    };

    this.cashflowChart = new Chart(canvas.nativeElement, config);
  }

  private _setupCarbonHarvestCanvasCharts(
    canvas: ElementRef<HTMLCanvasElement>
  ) {
    this.chartCanvasTwo = canvas;
    this.carbonHarvestChart?.destroy();

    const existingChart = Chart.getChart(canvas.nativeElement);
    existingChart?.destroy();

    const config: ChartConfiguration = {
      type: 'bar',
      data: {
        datasets: [
          {
            type: 'bar',
            label: 'ACCUs',
            data: this.accuArray,
            yAxisID: 'y-axis-left'
          },
          {
            type: 'bar',
            label: 'Total Log Products',
            data: this._peatProjection.yields.totalLogProducts,
            yAxisID: 'y-axis-right'
          }
        ],
        labels: this._xArr
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Carbon Credit & Harvest Yields'
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Plantation Age (Years)'
            }
          },
          'y-axis-right': {
            type: 'linear',
            position: 'right',
            title: {
              display: true,
              text: 'Harvest Yield (Tonnes)'
            },
            beginAtZero: true
          },
          'y-axis-left': {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: 'Carbon Credit Yield (ACCUs)'
            },
            beginAtZero: true,
            grid: {
              drawOnChartArea: false
            }
          }
        }
      }
    };
    this.carbonHarvestChart = new Chart(canvas.nativeElement, config);
  }

  private _calcArraySum(array: number[]) {
    return array.reduce((tot, curr) => tot + curr, 0);
  }

  private _calcTotalAndPerHa(array: number[], area: number): BaseRow {
    const total = array.reduce((tot, curr) => tot + curr, 0);
    const perHa = total / area;

    return new BaseRow(total, perHa);
  }

  private _sumAtIndex(obj: Costs | Returns, index: number): number {
    return Object.values(obj).reduce(
      (total, cur) => total + (cur[index] ?? 0),
      0
    );
  }

  private _getCosts(
    config: string[],
    fg: FormGroup,
    area: number,
    volumes: Map<string, number>,
    totalVolume: number
  ) {
    const harvestCostGroup = fg.get('harvestCost') as FormGroup;
    const distanceGroup = fg.get('distanceToMill') as FormGroup;
    const cartageGroup = fg.get('cartageCost') as FormGroup;

    const thinningHnCCost = config.reduce(
      (tot, curr) => {
        const harvestCostVal = this._getFormValue(harvestCostGroup, curr);
        const distanceVal = this._getFormValue(distanceGroup, curr);
        const cartageCostVal = this._getFormValue(cartageGroup, curr);
        const harvestVol = volumes.get(curr);

        const harvestCost = -harvestCostVal * harvestVol;
        const cartageCost = -distanceVal * harvestVol * cartageCostVal;

        return {
          harvestCost: tot.harvestCost + harvestCost,
          cartageCost: tot.cartageCost + cartageCost
        };
      },
      {
        harvestCost: 0,
        cartageCost: 0
      }
    );

    const harvestCost = thinningHnCCost.harvestCost * area;
    const cartageCost = thinningHnCCost.cartageCost * area;
    const fixedPlanningCost = -this._getFormValue(fg, 'fpp');
    const areaBasedPlanningCost =
      -this._getFormValue(fg, 'feesAndLevies') * area;
    const roadingCost =
      -this._getFormValue(fg, 'roadMaintenance') * area * totalVolume;

    return {
      harvestCost: harvestCost,
      cartageCost: cartageCost,
      fixedPlanningCost: fixedPlanningCost,
      areaBasedPlanningCost: areaBasedPlanningCost,
      roadingCost: roadingCost
    };
  }

  private _getReturns(
    config: string[],
    fg: FormGroup,
    area: number,
    volumes: Map<string, number>
  ): number[] {
    const logPriceGroup = fg.get('logPrice') as FormGroup;
    const returnsObj: number[] = [];

    config.forEach((item) => {
      returnsObj.push(
        this._getFormValue(logPriceGroup, item) * volumes.get(item) * area
      );
    });

    return returnsObj;
  }

  private _getFormValue(fg: FormGroup, ctrl: string): any {
    return fg.get(ctrl)!.value;
  }

  private _removeNullValues(obj: any) {
    if (obj === null) {
      return undefined;
    }

    if (typeof obj !== 'object' || Array.isArray(obj)) {
      return obj;
    }

    const result: any = {};

    Object.keys(obj).forEach((key) => {
      const value = this._removeNullValues(obj[key]);

      if (value !== undefined) {
        result[key] = value;
      }
    });

    return result;
  }

  private _setControlValue(
    fg: FormGroup,
    ctrlName: string,
    value: string | number
  ): void {
    fg.get(ctrlName).setValue(value, { emitEvent: false });
  }

  private _calculateAndSetControlValue(
    fg: FormGroup,
    ctrlName: string,
    total: number,
    ratio: number
  ): void {
    fg.get(ctrlName).setValue(Math.round(total * ratio), { emitEvent: false });
  }

  private _getSumSignal(fg: FormGroup, ctrlNames: string[]): Signal<any> {
    const signals = ctrlNames.map((n) => this._ctrlToSignal(fg, n));
    return computed(() => signals.reduce((sum, signal) => sum + signal(), 0));
  }

  private _ctrlToSignal(fg: FormGroup, ctrlName: string): any {
    let ctrl: any;
    if (ctrlName.includes('.')) {
      const [group, control] = ctrlName.split('.');
      ctrl = (fg.get(group) as FormGroup).get(control);
    } else {
      ctrl = fg.get(ctrlName) as FormControl;
    }

    return toSignal(ctrl.valueChanges, {
      initialValue: ctrl.value
    });
  }

  private _bindCalcSum(
    fg: FormGroup,
    targetCtrlName: string,
    sourceCtrlNames: string[]
  ) {
    const totalSignal = this._getSumSignal(fg, sourceCtrlNames);

    effect(() => {
      fg.get(targetCtrlName).setValue(totalSignal(), { emitEvent: false });
    });
  }

  private _matchesToDefault(
    fg: FormGroup,
    ctrlNames: string[],
    defaultVals: any,
    extraControls?: string[]
  ): Signal<boolean> {
    const controls = [...ctrlNames];
    if (extraControls) {
      controls.push(...extraControls);
    }
    const signals = controls.map((name) => ({
      name,
      signal: this._ctrlToSignal(fg, name)
    }));
    return computed(() =>
      signals.every(({ name, signal }) => {
        return signal() === defaultVals[name];
      })
    );
  }

  private _matchesToDefaultNested(
    fg: FormGroup,
    ctrlNames: string[],
    defaultVals: any
  ) {
    const signals = ctrlNames.map((name) => ({
      name,
      signal: this._ctrlToSignal(fg, name)
    }));

    return computed(() =>
      signals.every(({ name, signal }) => {
        const de = this._getNestedValue(defaultVals, name);
        const defaultVal =
          de && typeof de === 'object' && 'defaultValue' in de
            ? de.defaultValue
            : de;
        return signal() === defaultVal;
      })
    );
  }

  private _getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  private _thinningVolume() {
    const isCommercialThinning = this.modelCommercialThinning();
    this.thinRegime = this._getThinningRegime();
    const thinningDefaults = this._getThinningDefaults();
    const defaultCartageCost = thinningDefaults['cartageCost'];
    const { thinAge } = this._report;

    const productConfig = [
      {
        name: 'Small Sawlogs',
        controlName: 'smallSawlogs',
        volumeProp: 'smallSawLogsVolume',
        harvestVolumeProp: 'smallSawLogsHarvestVolume',
        cartageCostKey: 'smallSawlogs'
      },
      {
        name: 'Posts',
        controlName: 'posts',
        volumeProp: 'postsVolume',
        harvestVolumeProp: 'postsHarvestVolume',
        cartageCostKey: 'posts'
      },
      {
        name: 'Pulplogs',
        controlName: 'pulplogs',
        volumeProp: 'pulplogsVolume',
        harvestVolumeProp: 'pulplogsHarvestVolume',
        cartageCostKey: 'pulplogs'
      }
    ];

    const largeSawLogsThinningVolume = this._getRegimeYield(
      'Large Sawlogs',
      thinAge
    );

    this.thinningVolumes.set('largeSawlogs', largeSawLogsThinningVolume);

    const productData = productConfig.map((config) => {
      const product = this._getProductData(config.name);
      const volume = this._getRegimeYield(config.name, thinAge);
      this.thinningVolumes.set(config.controlName, volume);
      return {
        ...config,
        product,
        volume,
        harvestVolume: '',
        cartageCost: isCommercialThinning
          ? defaultCartageCost[config.cartageCostKey]
          : 0
      };
    });

    this.totalThinningVolume =
      largeSawLogsThinningVolume +
      productData.reduce((sum, item) => sum + item.volume, 0);

    for (const item of productData) {
      const harvestVolume = isCommercialThinning
        ? this._getPercentage(item.volume, this.totalThinningVolume)
        : this._getPercentage(0, 0);

      this[item.volumeProp] = item.volume;
      this[item.harvestVolumeProp] = harvestVolume;

      item.harvestVolume = harvestVolume;
    }
    const harvestVolumeGroup = this.thinning.get('harvestVolume') as FormGroup;
    const distanceGroup = this.thinning.get('distanceToMill') as FormGroup;
    const millLocationGroup = this.thinning.get('millLocation') as FormGroup;
    const cartageCostGroup = this.thinning.get('cartageCost') as FormGroup;

    for (const item of productData) {
      this._setControlValue(
        harvestVolumeGroup,
        item.controlName,
        item.harvestVolume
      );

      this._setControlValue(
        distanceGroup,
        item.controlName,
        item.product.distance
      );

      this._setControlValue(
        millLocationGroup,
        item.controlName,
        item.product.nearestMill
      );

      this._setControlValue(
        cartageCostGroup,
        item.controlName,
        item.cartageCost
      );
    }

    const thinningCtrls = [
      {
        ctrl: 'fpp',
        value: isCommercialThinning ? thinningDefaults['fpp'] : 0
      },
      {
        ctrl: 'feesAndLevies',
        value: isCommercialThinning ? thinningDefaults['feesAndLevies'] : 0
      },
      {
        ctrl: 'roadMaintenance',
        value: isCommercialThinning ? thinningDefaults['roadMaintenance'] : 0
      }
    ];
    for (const tc of thinningCtrls) {
      this._setControlValue(this.thinning, tc.ctrl, tc.value);
    }

    if (this.showSummary) {
      this.runReport(false);
    }
  }

  private _getLogTypeSum(name: string, age: number) {
    const thinning =
      this.assumedThinningAge === age ? this.thinningVolumes.get(name) : 0;
    const harvest =
      this.assumedHarvestAge === age ? this.harvestVolumes.get(name) : 0;

    return thinning + harvest;
  }

  private _getThinningRegime(): 'TH' | 'UT' {
    return this.modelCommercialThinning() ? 'TH' : 'UT';
  }

  private _harvestVolume() {
    const { harvestAge } = this._report;

    const productConfig = [
      {
        name: 'Large Sawlogs',
        controlName: 'largeSawlogs',
        volumeProp: 'largeSawLogsVolume',
        harvestVolumeProp: 'largeSawLogsHarvestVolume'
      },
      {
        name: 'Small Sawlogs',
        controlName: 'smallSawlogs',
        volumeProp: 'smallSawLogsVolume',
        harvestVolumeProp: 'smallSawLogsHarvestVolume'
      },
      {
        name: 'Pulplogs',
        controlName: 'pulplogs',
        volumeProp: 'pulplogsVolume',
        harvestVolumeProp: 'pulplogsHarvestVolume'
      }
    ];

    const postsHarvestVolume = this._getRegimeYield('Posts', harvestAge);
    this.harvestVolumes.set('posts', postsHarvestVolume);

    const productData = productConfig.map((config) => {
      const product = this._getProductData(config.name);
      const volume = this._getRegimeYield(config.name, harvestAge);
      this.harvestVolumes.set(config.controlName, volume);

      return {
        ...config,
        product,
        volume,
        harvestVolume: ''
      };
    });

    this.totalHarvestVolume =
      postsHarvestVolume +
      productData.reduce((sum, item) => sum + item.volume, 0);

    for (const item of productData) {
      const harvestVolume = this._getPercentage(
        item.volume,
        this.totalHarvestVolume
      );

      this[item.volumeProp] = item.volume;
      this[item.harvestVolumeProp] = harvestVolume;

      item.harvestVolume = harvestVolume;
    }

    const harvestVolumeGroup = this.harvest.get('harvestVolume') as FormGroup;
    const distanceGroup = this.harvest.get('distanceToMill') as FormGroup;
    const millLocationGroup = this.harvest.get('millLocation') as FormGroup;

    for (const item of productData) {
      this._setControlValue(
        harvestVolumeGroup,
        item.controlName,
        item.harvestVolume
      );

      this._setControlValue(
        distanceGroup,
        item.controlName,
        item.product.distance
      );

      this._setControlValue(
        millLocationGroup,
        item.controlName,
        item.product.nearestMill
      );
    }
  }

  private _getRegimeYield(name: string, age: number): number {
    const getProduct = (name: string) => this._getProductData(name);

    const product = getProduct(name);

    const regimeYield = product.pineEconomicAnalysisHarvestYieldRows?.find(
      (row) => row.thinRegime === this.thinRegime
    );
    return regimeYield?.harvestYieldAgeOfPlantation?.[age] ?? 0;
  }

  private _getProductData(name: string): PineEconomicAnalysisToolRow {
    return this._report.pineEconomicAnalysisRows.find(
      (s) => s.product === name
    );
  }
  private _getThinningDefaults() {
    const thinningDefaults = getDefaultValObj('thinning', this.defaultValues);

    const thinningCtrls: CtrlConfig[] = this._getThinningCtrls();

    thinningCtrls.forEach((ctrl) => {
      const analysisR = this._report.pineEconomicAnalysisRows.find(
        (a) => a.product == ctrl.name
      );
      thinningDefaults['distanceToMill'][ctrl.controlName] = analysisR.distance;
    });

    return thinningDefaults;
  }

  private _getHarvestDefaults() {
    const harvestDefaults = getDefaultValObj('harvest', this.defaultValues);

    const harvestCtrls: CtrlConfig[] = this._getHarvestCtrls();

    harvestCtrls.forEach((ctrl) => {
      const analysisR = this._report.pineEconomicAnalysisRows.find(
        (a) => a.product == ctrl.name
      );
      harvestDefaults['distanceToMill'][ctrl.controlName] = analysisR.distance;
    });

    return harvestDefaults;
  }

  private _getPercentage(
    valueOne: number,
    valueTwo: number,
    decimal = 2
  ): string {
    if (valueTwo === 0) {
      return '0.00';
    }
    return ((valueOne / valueTwo) * 100).toFixed(decimal);
  }

  private _defaultDistanceVal(fg: FormGroup, ctrls: any[]) {
    const distanceGroup = fg.get('distanceToMill') as FormGroup;

    for (const ctrl of ctrls) {
      const analysisR = this._report.pineEconomicAnalysisRows.find(
        (a) => a.product == ctrl.name
      );
      this._setControlValue(
        distanceGroup,
        ctrl.controlName,
        analysisR.distance
      );
    }
  }

  private _getThinningCtrls(): CtrlConfig[] {
    return [
      {
        name: 'Small Sawlogs',
        controlName: 'smallSawlogs'
      },
      {
        name: 'Posts',
        controlName: 'posts'
      },
      {
        name: 'Pulplogs',
        controlName: 'pulplogs'
      }
    ];
  }

  private _getHarvestCtrls(): CtrlConfig[] {
    return [
      {
        name: 'Large Sawlogs',
        controlName: 'largeSawlogs'
      },
      {
        name: 'Small Sawlogs',
        controlName: 'smallSawlogs'
      },
      {
        name: 'Pulplogs',
        controlName: 'pulplogs'
      }
    ];
  }

  private _createAndInitialiseForm() {
    this.establishment = this.formBuilder.group({
      allInclusiveCost: [
        this.getDefaultVal('establishment')['allInclusiveCost'],
        [Validators.required]
      ],
      sitePreparation: [
        this.getDefaultVal('establishment')['sitePreparation'],
        [Validators.required]
      ],
      weedControl: [
        this.getDefaultVal('establishment')['weedControl'],
        [Validators.required]
      ],
      seedlings: [
        this.getDefaultVal('establishment')['seedlings'],
        [Validators.required]
      ],
      planting: [
        this.getDefaultVal('establishment')['planting'],
        [Validators.required]
      ],
      fertilising: [
        this.getDefaultVal('establishment')['fertilising'],
        [Validators.required]
      ],
      gameControl: [
        this.getDefaultVal('establishment')['gameControl'],
        [Validators.required]
      ],
      fpp: [this.getDefaultVal('establishment')['fpp'], [Validators.required]],
      feesAndLevies: [
        this.getDefaultVal('establishment')['feesAndLevies'],
        [Validators.required]
      ],
      ptr: [this.getDefaultVal('establishment')['ptr'], [Validators.required]]
    });
    this.establishmentFgSignal = toSignal(this.establishment.valueChanges);

    this.allInclusiveCostSignal = toSignal(
      this.establishment.get('allInclusiveCost').valueChanges
    );

    this.establishmentTotalSignal = this._getSumSignal(
      this.establishment,
      this.establishmentControls
    );

    this.useEstablishmentDefault = this._matchesToDefault(
      this.establishment,
      this.establishmentControls,
      this.getDefaultVal('establishment'),
      ['allInclusiveCost', 'fpp', 'feesAndLevies', 'ptr']
    );

    this.postEstablishment = this.formBuilder.group({
      total: [
        this.getDefaultVal('postEstablishment')['total'],
        [Validators.required]
      ],
      weedControl: [
        this.getDefaultVal('postEstablishment')['weedControl'],
        [Validators.required]
      ],
      refillPlanting: [
        this.getDefaultVal('postEstablishment')['refillPlanting'],
        [Validators.required]
      ],
      gameControl: [
        this.getDefaultVal('postEstablishment')['gameControl'],
        [Validators.required]
      ],
      complianceReporting: [
        this.getDefaultVal('postEstablishment')['complianceReporting'],
        [Validators.required]
      ],
      annualMaintenance: [
        this.getDefaultVal('postEstablishment')['annualMaintenance'],
        [Validators.required]
      ]
    });
    this.postEstablishmentFgSignal = toSignal(
      this.postEstablishment.valueChanges
    );
    this.totalSignal = toSignal(
      this.postEstablishment.get('total').valueChanges
    );
    this.postEstablishmentTotalSignal = this._getSumSignal(
      this.postEstablishment,
      this.postEstablishmentControls
    );
    this.usePostEstablishmentDefault = this._matchesToDefault(
      this.postEstablishment,
      this.postEstablishmentControls,
      this.getDefaultVal('postEstablishment'),
      ['total', 'annualMaintenance']
    );

    this.carbon = this.formBuilder.group({
      establishmentCost: [
        this.getDefaultVal('carbon')['establishmentCost'],
        [Validators.required]
      ],
      annualOffset: [
        this.getDefaultVal('carbon')['annualOffset'],
        [Validators.required]
      ],
      audit: [this.getDefaultVal('carbon')['audit'], [Validators.required]],
      accu: [this.getDefaultVal('carbon')['accu'], [Validators.required]]
    });
    this.carbonFgSignal = toSignal(this.carbon.valueChanges);
    this.useCarbonDefault = this._matchesToDefault(
      this.carbon,
      this.carbonControls,
      this.getDefaultVal('carbon')
    );

    this.thinning = this.formBuilder.group({
      harvestVolume: this.formBuilder.group({
        smallSawlogs: [{ value: 0, disabled: true }, []],
        posts: [{ value: 0, disabled: true }, []],
        pulplogs: [{ value: 0, disabled: true }, []]
      }),
      harvestCost: this.formBuilder.group({
        smallSawlogs: [[Validators.required]],
        posts: [[Validators.required]],
        pulplogs: [[Validators.required]]
      }),
      distanceToMill: this.formBuilder.group({
        smallSawlogs: [[Validators.required]],
        posts: [[Validators.required]],
        pulplogs: [[Validators.required]]
      }),
      millLocation: this.formBuilder.group({
        smallSawlogs: [{ value: '', disabled: true }, []],
        posts: [{ value: '', disabled: true }, []],
        pulplogs: [{ value: '', disabled: true }, []]
      }),
      cartageCost: this.formBuilder.group({
        smallSawlogs: [[Validators.required]],
        posts: [[Validators.required]],
        pulplogs: [[Validators.required]]
      }),
      logPrice: this.formBuilder.group({
        smallSawlogs: [[Validators.required]],
        posts: [[Validators.required]],
        pulplogs: [[Validators.required]]
      }),
      fpp: [[Validators.required]],
      feesAndLevies: [[Validators.required]],
      roadMaintenance: [[Validators.required]]
    });
    this.thinningFgSignal = toSignal(this.thinning.valueChanges);

    this.harvest = this.formBuilder.group({
      harvestVolume: this.formBuilder.group({
        largeSawlogs: [{ value: 0, disabled: true }, []],
        smallSawlogs: [{ value: 0, disabled: true }, []],
        pulplogs: [{ value: 0, disabled: true }, []]
      }),
      harvestCost: this.formBuilder.group({
        largeSawlogs: [[Validators.required]],
        smallSawlogs: [[Validators.required]],
        pulplogs: [[Validators.required]]
      }),
      distanceToMill: this.formBuilder.group({
        largeSawlogs: [[Validators.required]],
        smallSawlogs: [[Validators.required]],
        pulplogs: [[Validators.required]]
      }),
      millLocation: this.formBuilder.group({
        largeSawlogs: [{ value: '', disabled: true }, []],
        smallSawlogs: [{ value: '', disabled: true }, []],
        pulplogs: [{ value: '', disabled: true }, []]
      }),
      cartageCost: this.formBuilder.group({
        largeSawlogs: [[]],
        smallSawlogs: [[]],
        pulplogs: [[]]
      }),
      logPrice: this.formBuilder.group({
        largeSawlogs: [[]],
        smallSawlogs: [[]],
        pulplogs: [[]]
      }),
      fpp: [[Validators.required]],
      feesAndLevies: [[Validators.required]],
      roadMaintenance: [[Validators.required]]
    });
    this.harvestFgSignal = toSignal(this.harvest.valueChanges);

    this.financial = this.formBuilder.group({
      discountRate: [
        this.getDefaultVal('financial')['discountRate'],
        [Validators.required]
      ]
    });
    this.financialFgSignal = toSignal(this.financial.valueChanges);
    this.useFinancialDefault = this._matchesToDefault(
      this.financial,
      ['discountRate'],
      this.getDefaultVal('financial')
    );

    this.thinningData = [
      {
        product: '% of harvest volume',
        formGroup: this.thinning.get('harvestVolume') as FormGroup,
        type: 'number'
      },
      {
        product: 'Harvest Cost ($ per tonne)',
        formGroup: this.thinning.get('harvestCost') as FormGroup,
        showInfo: true,
        info: 'thinningHarvestCost',
        showDollar: true,
        dollar: 'thinningHarvestCost',
        type: 'number'
      },
      {
        product: 'Distance to mill (km)',
        formGroup: this.thinning.get('distanceToMill') as FormGroup,
        showInfo: true,
        info: 'thinningDistanceToMill',
        type: 'number'
      },
      {
        product: 'Mill Location',
        formGroup: this.thinning.get('millLocation') as FormGroup,
        type: 'text'
      },
      {
        product: 'Cartage Cost ($ per tonne per km)',
        formGroup: this.thinning.get('cartageCost') as FormGroup,
        type: 'number',
        step: 0.01
      },
      {
        product: 'Log price ($ per tonne)',
        formGroup: this.thinning.get('logPrice') as FormGroup,
        showInfo: true,
        type: 'number',
        showDollar: true,
        dollar: 'thinningLogPrice',
        info: 'thinningLogPrice'
      }
    ];
    this.harvestData = [
      {
        product: '% of harvest volume',
        formGroup: this.harvest.get('harvestVolume') as FormGroup,
        type: 'number'
      },
      {
        product: 'Harvest Cost ($ per tonne)',
        formGroup: this.harvest.get('harvestCost') as FormGroup,
        showInfo: true,
        info: 'harvestCost',
        showDollar: true,
        dollar: 'harvestCost',
        type: 'number'
      },
      {
        product: 'Distance to mill (km)',
        formGroup: this.harvest.get('distanceToMill') as FormGroup,
        info: 'thinningDistanceToMill',
        type: 'number'
      },
      {
        product: 'Mill Location',
        formGroup: this.harvest.get('millLocation') as FormGroup,
        type: 'text'
      },
      {
        product: 'Cartage Cost ($ per tonne per km)',
        formGroup: this.harvest.get('cartageCost') as FormGroup,
        type: 'number',
        step: 0.01
      },
      {
        product: 'Log price ($ per tonne)',
        formGroup: this.harvest.get('logPrice') as FormGroup,
        type: 'number',
        showInfo: true,
        info: 'harvestLogPrice',
        showDollar: true,
        dollar: 'harvestLogPrice'
      }
    ];
  }

  protected readonly fetch = fetch;
}

export interface TableRow {
  product: string;
  formGroup: FormGroup;
  type: 'text' | 'number';
  showInfo?: boolean;
  info?: string;
  showDollar?: boolean;
  dollar?: string;
  step?: 1 | 0.01;
}

export interface CtrlConfig {
  name: string;
  controlName: string;
}
